import type { AnalyticsMeta, AnalyticsSource } from "@/types/analytics";

/**
 * Freshness, as-of and source copy for analytics tiles (ANL-E04-S09, CMP-11.09, CMP-11.19,
 * FR-ADM-16, FR-ADM-18, FR-ADM-19). Pure TypeScript with no React import, so it runs in the
 * DOM-less unit runner. The browser holds no freshness targets: the target and the Delayed band
 * come from `meta` (ADM-OI-06), and with no run-ledger row a summary or event tile is never Fresh
 * (FR-VAL-14), so every such tile reads "Freshness unknown" until ANL-E05 sends one.
 */

/** S daily summary, E event rows, L ledger, O product tables, R the plan as deployed. */
export type SourceKind = "S" | "E" | "L" | "O" | "R";

// Engine-neutral by rule (AC2): no screen text names the store.
export const SOURCE_KIND_WORDS: Readonly<Record<SourceKind, string>> = {
  S: "daily summary",
  E: "event rows",
  L: "ledger",
  O: "product records, not event-derived",
  R: "plan as deployed",
};

export const SOURCE_KIND_OF: Readonly<Record<AnalyticsSource, SourceKind>> = {
  summary: "S",
  events: "E",
  ledger: "L",
  product: "O",
  registry: "R",
};

/** The run-ledger facts a chip needs; ANL-E05 sends them. */
export type RunLedgerRow = {
  // Why the latest run failed ("timeout"); null when it succeeded. A failed run leaves `asOf` at
  // the last good run, so the chip's tone still follows that run's age (UXS-09).
  latestRunFailure: string | null;
};

/**
 * `meta` as the chip reads it. `run`, `delayedBandFactor` and `dataThrough` are not sent today
 * (ANL-E04-S04 sends the response-level meta only); each is read when present.
 */
export type FreshnessMeta = AnalyticsMeta & {
  run?: RunLedgerRow | null;
  // The Delayed band as a multiple of the target, an owner setting (ADM-OI-06).
  delayedBandFactor?: number | null;
  // The last day the numbers cover, for kinds S and E.
  dataThrough?: string | null;
};

export type FreshnessState = "fresh" | "delayed" | "stale" | "unknown" | "live" | "plan";

export type FreshnessChip = {
  state: FreshnessState;
  kind: SourceKind;
  label: string;
  className: string;
  // "Latest run failed: <reason>" under the chip, or null.
  subLine: string | null;
};

export const NO_AS_OF_TIME = "No as-of time returned";
const FRESHNESS_UNKNOWN = "Freshness unknown";
const PLAN_AS_DEPLOYED = "Plan as deployed";
// AC3's band when the server sends none: Delayed up to 3 times the target.
const DEFAULT_DELAYED_BAND_FACTOR = 3;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const LIVE_KINDS: ReadonlySet<SourceKind> = new Set(["L", "O"]);
const DATED_KINDS: ReadonlySet<SourceKind> = new Set(["S", "E"]);

// Colour never carries the meaning alone: every label carries its state word. The neutral chip
// adds `--ink`, whose text reads 4.5:1 or more on the sand fill (DSU-01, ANL-E04-S11).
const CHIP_CLASS: Readonly<Record<FreshnessState, string>> = {
  fresh: "pz-chip pz-chip--positive",
  delayed: "pz-chip pz-chip--accent",
  stale: "pz-chip pz-chip--negative",
  unknown: "pz-chip pz-chip--quiet",
  live: "pz-chip pz-chip--neutral pz-chip--ink",
  plan: "pz-chip pz-chip--neutral pz-chip--ink",
};

// The page chip's "worst": a known-stale tile first, then a tile nobody can vouch for, then a late
// one. Live and the plan are read at query time and are never worse than a checked summary.
const SEVERITY: readonly FreshnessState[] = ["plan", "live", "fresh", "delayed", "unknown", "stale"];

// The page chip's words when the page mixes sources: no time and no age.
const UNTIMED_LABEL: Readonly<Record<FreshnessState, string>> = {
  fresh: "Fresh",
  delayed: "Delayed",
  stale: "Stale",
  unknown: FRESHNESS_UNKNOWN,
  live: "Live",
  plan: PLAN_AS_DEPLOYED,
};

const timeOf = (iso: unknown): number | null => {
  if (typeof iso !== "string") return null;
  const time = Date.parse(iso);
  return Number.isFinite(time) ? time : null;
};

const utcHoursMinutes = (time: number): string => new Date(time).toISOString().slice(11, 16);

/** "42 min", "2 h" or "1 d": the age a Delayed chip prints, in its largest whole unit. */
const formatAge = (ageMs: number): string => {
  if (ageMs >= DAY_MS) return `${Math.floor(ageMs / DAY_MS)} d`;
  if (ageMs >= HOUR_MS) return `${Math.floor(ageMs / HOUR_MS)} h`;
  return `${Math.floor(ageMs / MINUTE_MS)} min`;
};

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const chip = (state: FreshnessState, kind: SourceKind, label: string, subLine: string | null = null): FreshnessChip => ({
  state,
  kind,
  label,
  className: CHIP_CLASS[state],
  subLine,
});

/** The ledger-backed chip of a summary or event tile: Fresh, Delayed, Stale or unknown. */
const selectCheckedFreshness = (meta: FreshnessMeta, kind: SourceKind, asOf: number, now: number): FreshnessChip => {
  const target = meta.freshTargetMs;
  if (!meta.run || !isPositiveNumber(target)) return chip("unknown", kind, FRESHNESS_UNKNOWN);

  const failure = meta.run.latestRunFailure;
  const subLine = failure ? `Latest run failed: ${failure}` : null;
  const band = isPositiveNumber(meta.delayedBandFactor) ? meta.delayedBandFactor : DEFAULT_DELAYED_BAND_FACTOR;
  const ageMs = Math.max(0, now - asOf);
  const time = utcHoursMinutes(asOf);
  if (ageMs <= target) return chip("fresh", kind, `Fresh, as of ${time} UTC`, subLine);
  if (ageMs <= band * target) return chip("delayed", kind, `Delayed ${formatAge(ageMs)}`, subLine);
  return chip("stale", kind, `Stale, last good ${time} UTC`, subLine);
};

/** The freshness chip of one tile (AC3), at `now` (epoch ms). */
export const selectFreshness = (meta: FreshnessMeta, now: number): FreshnessChip => {
  const kind = SOURCE_KIND_OF[meta.source] ?? "S";
  if (kind === "R") return chip("plan", kind, PLAN_AS_DEPLOYED);
  const asOf = timeOf(meta.asOf);
  if (asOf === null) return chip("unknown", kind, FRESHNESS_UNKNOWN);
  if (LIVE_KINDS.has(kind)) return chip("live", kind, `Live, ${utcHoursMinutes(asOf)} UTC`);
  return selectCheckedFreshness(meta, kind, asOf, now);
};

/**
 * The page chip (AC3): the worst tile state. It keeps that tile's label when every tile reads the
 * same kind of source, and drops the time when the page mixes kinds. null with no tiles.
 */
export const selectPageFreshness = (chips: readonly FreshnessChip[]): FreshnessChip | null => {
  if (chips.length === 0) return null;
  let worst = chips[0];
  for (const candidate of chips) {
    if (SEVERITY.indexOf(candidate.state) > SEVERITY.indexOf(worst.state)) worst = candidate;
  }
  const mixed = chips.some((candidate) => candidate.kind !== worst.kind);
  return mixed ? { ...worst, label: UNTIMED_LABEL[worst.state], subLine: null } : worst;
};

export type AsOfFooter = { ok: true; text: string } | { ok: false; message: typeof NO_AS_OF_TIME };

/**
 * A tile's footer (AC4): the oldest as-of of the responses behind it, "Live" when every one is
 * read at query time. With no meta or no readable asOf it is Error: never a value without a time.
 */
export const buildAsOfFooter = (metas: ReadonlyArray<FreshnessMeta | null>): AsOfFooter => {
  let oldest: number | null = null;
  let live = true;
  for (const meta of metas) {
    const asOf = meta ? timeOf(meta.asOf) : null;
    if (meta === null || asOf === null) return { ok: false, message: NO_AS_OF_TIME };
    if (oldest === null || asOf < oldest) oldest = asOf;
    live = live && LIVE_KINDS.has(SOURCE_KIND_OF[meta.source]);
  }
  if (oldest === null) return { ok: false, message: NO_AS_OF_TIME };
  const time = utcHoursMinutes(oldest);
  return { ok: true, text: live ? `Live, ${time} UTC` : `as of ${time} UTC` };
};

const isoDateOf = (value: string | null | undefined): string | null => {
  const time = timeOf(value);
  return time === null ? null : new Date(time).toISOString().slice(0, 10);
};

/** The source popover's rows (AC2): the source words, the as-of time and, for S and E, the data-through date. */
export const sourceInfoRows = (kind: SourceKind, meta: FreshnessMeta | null): string[] => {
  const asOf = meta ? timeOf(meta.asOf) : null;
  const rows = [
    `Source: ${SOURCE_KIND_WORDS[kind]}`,
    `As of: ${asOf === null ? NO_AS_OF_TIME : `${utcHoursMinutes(asOf)} UTC`}`,
  ];
  if (DATED_KINDS.has(kind)) {
    rows.push(`Data through: ${isoDateOf(meta?.dataThrough) ?? "not returned"}`);
  }
  return rows;
};
